import { useState, useMemo } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { PieChart, GitBranch, Grid3X3, Search, BookOpen } from "lucide-react";
import type { VisualSeed, VisualNode } from "@/hooks/useMapVisualData";
import { MapStatsCards } from "./MapStatsCards";
import { StatusGrid } from "./StatusGrid";
import { SunburstChart } from "./SunburstChart";
import { ForceGraph } from "./ForceGraph";
import { ContentBrowser } from "./ContentBrowser";
import { ROLE_COLORS, ROLE_LABELS, ENGINE_STAGES, STAGE_LABELS } from "./constants";

interface MapVisualDashboardProps {
  mapName: string;
  seeds: VisualSeed[];
  nodes: VisualNode[];
}

export function MapVisualDashboard({ mapName, seeds, nodes }: MapVisualDashboardProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
  const [selectedStages, setSelectedStages] = useState<Set<string>>(new Set());
  const [selectedSeeds, setSelectedSeeds] = useState<Set<string>>(new Set());
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  // Get unique roles in this map
  const activeRoles = useMemo(() => {
    const roles = new Set<string>();
    for (const n of nodes) roles.add(n.document_role);
    return [...roles].sort();
  }, [nodes]);

  // Get unique stages
  const activeStages = useMemo(() => {
    const stages = new Set<string>();
    for (const n of nodes) stages.add(n.engine_stage || "pending");
    return [...stages];
  }, [nodes]);

  // Filter nodes
  const filteredNodeIds = useMemo(() => {
    const hasFilters = searchQuery || selectedRoles.size > 0 || selectedStages.size > 0 || selectedSeeds.size > 0;
    if (!hasFilters) return null; // null = no filter (show all)

    const ids = new Set<string>();
    for (const n of nodes) {
      if (searchQuery && !n.title.toLowerCase().includes(searchQuery.toLowerCase())) continue;
      if (selectedRoles.size > 0 && !selectedRoles.has(n.document_role)) continue;
      if (selectedStages.size > 0 && !selectedStages.has(n.engine_stage || "pending")) continue;
      if (selectedSeeds.size > 0 && !selectedSeeds.has(n.seed_id)) continue;
      ids.add(n.id);
    }
    return ids;
  }, [nodes, searchQuery, selectedRoles, selectedStages, selectedSeeds]);

  const toggleFilter = (set: Set<string>, value: string, setter: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value); else next.add(value);
    setter(next);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedRoles(new Set());
    setSelectedStages(new Set());
    setSelectedSeeds(new Set());
  };

  const hasFilters = searchQuery || selectedRoles.size > 0 || selectedStages.size > 0 || selectedSeeds.size > 0;
  const filteredCount = filteredNodeIds ? filteredNodeIds.size : nodes.length;

  return (
    <div className="space-y-4">
      <MapStatsCards seeds={seeds} nodes={nodes} />

      {/* Filters toolbar */}
      <div className="rounded-lg border bg-card p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Szukaj node po tytule..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 text-sm max-w-[300px]"
          />
          {hasFilters && (
            <Badge variant="secondary" className="text-xs cursor-pointer" onClick={clearFilters}>
              {filteredCount}/{nodes.length} nodes — wyczysc filtry
            </Badge>
          )}
        </div>

        {/* Role filter */}
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-xs text-muted-foreground mr-1">Rola:</span>
          {activeRoles.map(role => (
            <Badge
              key={role}
              variant={selectedRoles.has(role) ? "default" : "outline"}
              className="text-[10px] cursor-pointer px-1.5 py-0"
              style={{
                borderColor: ROLE_COLORS[role],
                color: selectedRoles.has(role) ? "white" : ROLE_COLORS[role],
                backgroundColor: selectedRoles.has(role) ? ROLE_COLORS[role] : "transparent",
              }}
              onClick={() => toggleFilter(selectedRoles, role, setSelectedRoles)}
            >
              {ROLE_LABELS[role] || role}
            </Badge>
          ))}
        </div>

        {/* Stage filter */}
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-xs text-muted-foreground mr-1">Status:</span>
          {activeStages.map(stage => (
            <Badge
              key={stage}
              variant={selectedStages.has(stage) ? "default" : "outline"}
              className="text-[10px] cursor-pointer px-1.5 py-0"
              onClick={() => toggleFilter(selectedStages, stage, setSelectedStages)}
            >
              {STAGE_LABELS[stage] || stage}
            </Badge>
          ))}
        </div>

        {/* Seed filter */}
        {seeds.length > 3 && (
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-xs text-muted-foreground mr-1">Seed:</span>
            {seeds.map(seed => (
              <Badge
                key={seed.id}
                variant={selectedSeeds.has(seed.id) ? "default" : "outline"}
                className="text-[10px] cursor-pointer px-1.5 py-0"
                onClick={() => toggleFilter(selectedSeeds, seed.id, setSelectedSeeds)}
              >
                {seed.name.length > 20 ? seed.name.slice(0, 17) + "..." : seed.name}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Views */}
      <Tabs defaultValue="grid" className="space-y-3">
        <TabsList>
          <TabsTrigger value="grid" className="gap-1.5">
            <Grid3X3 className="h-3.5 w-3.5" /> Status Grid
          </TabsTrigger>
          <TabsTrigger value="sunburst" className="gap-1.5">
            <PieChart className="h-3.5 w-3.5" /> Sunburst
          </TabsTrigger>
          <TabsTrigger value="force" className="gap-1.5">
            <GitBranch className="h-3.5 w-3.5" /> Force Graph
          </TabsTrigger>
          <TabsTrigger value="content" className="gap-1.5">
            <BookOpen className="h-3.5 w-3.5" /> Briefy & Tresci
          </TabsTrigger>
        </TabsList>

        <TabsContent value="grid">
          <StatusGrid seeds={seeds} nodes={filteredNodeIds ? nodes.filter(n => filteredNodeIds.has(n.id)) : nodes} onNodeClick={setSelectedNodeId} />
        </TabsContent>

        <TabsContent value="sunburst">
          <SunburstChart mapName={mapName} seeds={seeds} nodes={nodes} filteredNodeIds={filteredNodeIds} onNodeClick={setSelectedNodeId} />
        </TabsContent>

        <TabsContent value="force">
          <ForceGraph seeds={seeds} nodes={nodes} filteredNodeIds={filteredNodeIds} onNodeClick={setSelectedNodeId} />
        </TabsContent>

        <TabsContent value="content">
          <ContentBrowser seeds={seeds} nodes={filteredNodeIds ? nodes.filter(n => filteredNodeIds.has(n.id)) : nodes} />
        </TabsContent>
      </Tabs>

      {/* Selected node detail */}
      {selectedNodeId && (() => {
        const node = nodes.find(n => n.id === selectedNodeId);
        if (!node) return null;
        const seed = seeds.find(s => s.id === node.seed_id);
        return (
          <div className="fixed bottom-4 right-4 w-96 rounded-xl border bg-card shadow-2xl p-4 z-50">
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-medium text-sm leading-tight pr-4">{node.title}</h3>
              <button onClick={() => setSelectedNodeId(null)} className="text-muted-foreground hover:text-foreground text-xs">X</button>
            </div>
            <div className="space-y-1 text-xs text-muted-foreground">
              <p><span className="font-medium text-foreground">Seed:</span> {seed?.name || "?"}</p>
              <p><span className="font-medium text-foreground">Rola:</span> <span style={{ color: ROLE_COLORS[node.document_role] }}>{ROLE_LABELS[node.document_role] || node.document_role}</span></p>
              <p><span className="font-medium text-foreground">Encja:</span> {node.primary_entity_raw}</p>
              <p><span className="font-medium text-foreground">Status:</span> {STAGE_LABELS[node.engine_stage || "pending"]}</p>
              {node.quality_score != null && <p><span className="font-medium text-foreground">QA:</span> {node.quality_score}/100</p>}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
